// @flow

import React, { type Node } from 'react';
import type { Country } from '@tectonics/prs/country';
import type { PaymentMethodCode } from '@tectonics/prs/payment-method';
import { translate } from '@tectonics/translate';

type PaymentMethodNameProps = {|
  paymentMethod: PaymentMethodCode,
  country: Country,
|};

export function PaymentMethodName({ paymentMethod, country }: PaymentMethodNameProps): Node {
  switch (country) {
    case 'br':
      return <BrazilianMethodName paymentMethod={paymentMethod} />;
    case 'ar':
      return <ArgentineMethodName paymentMethod={paymentMethod} />;
    case 'bo':
      return <BolivianMethodName paymentMethod={paymentMethod} />;
    case 'cl':
      return <ChileanMethodName paymentMethod={paymentMethod} />;
    case 'co':
      return <ColombianMethodName paymentMethod={paymentMethod} />;
    case 'ec':
      return <EcuadorianMethodName paymentMethod={paymentMethod} />;
    case 'mx':
      return <MexicanMethodName paymentMethod={paymentMethod} />;
    case 'pe':
      return <PeruvianMethodName paymentMethod={paymentMethod} />;
    case 'uy':
      return <UruguayanMethodName paymentMethod={paymentMethod} />;
    case 'pa':
      return <PanamanianMethodName paymentMethod={paymentMethod} />;
    case 'sv':
      return <ElSalvadorMethodName paymentMethod={paymentMethod} />;
    case 'gt':
      return <GuatemalanMethodName paymentMethod={paymentMethod} />;
    case 'do':
      return <DominicanMethodName paymentMethod={paymentMethod} />;
    case 'cr':
      return <CostaRicanMethodName paymentMethod={paymentMethod} />;
    case 'py':
      return <ParaguayanMethodName paymentMethod={paymentMethod} />;
    case 'za':
      return <SouthAfricanMethodName paymentMethod={paymentMethod} />;
    case 'ng':
      return <NigerianMethodName paymentMethod={paymentMethod} />;
    case 'ke':
      return <KenyanMethodName paymentMethod={paymentMethod} />;
    case 'in':
      return <IndiaMethodName paymentMethod={paymentMethod} />;
    case 'tz':
    case 'ug':
    case 'zm':
    case 'gh':
      return <MobileMoneyMethodName paymentMethod={paymentMethod} />;
    case 'eg':
      return <EgyptMethodName paymentMethod={paymentMethod} />;
    case 'jm':
      return <JamaicaMethodName paymentMethod={paymentMethod} />;
    default:
      throw new Error(`Country ${country} has no name for method ${paymentMethod} implemented yet.`);
  }
}

type CountryPaymentMethodNameProps = {|
  paymentMethod: PaymentMethodCode,
|};

function BrazilianMethodName({ paymentMethod }: CountryPaymentMethodNameProps): Node {
  switch (paymentMethod) {
    case 'creditcard':
      return 'Cartão de Crédito';

    case 'boleto':
      return 'Boleto';

    case 'banktransfer':
      return 'Transferência Bancária';

    case 'tef':
      return 'Internet Banking';

    case 'google':
      return 'Google Pay';

    case 'debitcard':
      return 'Cartão de Débito';

    case 'ewallet':
      return 'Carteiras Digitais';

    case 'pix':
      return 'Pix';

    case 'vouchercard':
      return 'Vale Refeição';

    case 'multibeneficios':
      return 'Vale Multibenefícios';

    case 'nupay':
      return 'Nubank (até 24x)';

    case 'mpcredito':
      return 'Mercado Crédito (Pix em até 12x)';

    default:
      throw new Error(`Payment method ${paymentMethod} has no name implemented yet.`);
  }
}

function MexicanMethodName({ paymentMethod }: CountryPaymentMethodNameProps): Node {
  switch (paymentMethod) {
    case 'creditcard':
      return 'Tarjeta de crédito';

    case 'oxxo':
      return 'OXXO';

    case 'oxxopay':
      return 'OXXOPay';

    case 'cash':
      return 'Pago vía Paynet';

    case 'spei':
      return 'SPEI';

    case 'debitcard':
      return 'Tarjeta de débito';

    case 'ewallet':
      return 'Mercado Pago';

    case 'google':
      return 'Google Pay';

    case 'googleV2':
      return 'Google Pay V2';

    case 'externalpayprovider':
      return 'Aplazo';

    default:
      throw new Error(`Payment method ${paymentMethod} has no name implemented yet.`);
  }
}

function ArgentineMethodName({ paymentMethod }: CountryPaymentMethodNameProps): Node {
  switch (paymentMethod) {
    case 'creditcard':
      return 'Tarjeta de crédito';
    case 'debitcard':
      return 'Tarjeta de débito';
    case 'cash':
      return 'Efectivo';
    case 'ewallet':
      return 'Mercado Pago';
    case 'banktransfer':
      return 'Debin';
    default:
      throw new Error(`Payment method ${paymentMethod} has no name implemented yet.`);
  }
}

function ColombianMethodName({ paymentMethod }: CountryPaymentMethodNameProps): Node {
  switch (paymentMethod) {
    case 'eft':
      return 'Pagos Seguros en Línea (PSE)';

    case 'creditcard':
      return 'Tarjeta de crédito';

    case 'debitcard':
      return 'Tarjeta de débito';

    case 'cash':
      return 'Efecty';

    case 'ewallet':
      return 'NEQUI';

    case 'externalpayprovider':
      return 'Transferencia Electrónica';

    default:
      throw new Error(`Payment method ${paymentMethod} has no name implemented yet.`);
  }
}


function ChileanMethodName({ paymentMethod }: CountryPaymentMethodNameProps): Node {
  switch (paymentMethod) {
    case 'creditcard':
      return 'Tarjeta de crédito';
    case 'multicaja':
      return 'Klap (Multicaja)';
    case 'webpay':
      return 'WebPay';
    case 'sencillito':
      return 'Sencillito';
    case 'servipag':
      return 'ServiPag';
    case 'ewallet':
      return 'Billeteras electrónicas';
    case 'google':
      return 'Google Pay';
    case 'banktransfer':
      return 'Transferencia bancaria';
    default:
      throw new Error(`Payment method ${paymentMethod} has no name implemented yet.`);
  }
}

function BolivianMethodName({ paymentMethod }: CountryPaymentMethodNameProps): Node {
  switch (paymentMethod) {
    case 'cash':
      return 'Efectivo';
    default:
      throw new Error(`Payment method ${paymentMethod} has no name implemented yet.`);
  }
}

function PeruvianMethodName({ paymentMethod }: CountryPaymentMethodNameProps): Node {
  switch (paymentMethod) {
    case 'pagoefectivo':
      return 'PagoEfectivo';
    case 'safetypay-cash':
      return 'Efectivo (vía SafetyPay)';
    case 'safetypay-online':
      return 'Débito en Línea (vía SafetyPay)';
    case 'creditcard':
      return 'Tarjeta de Crédito o Débito';
    case 'banktransfer':
      return 'Transferencia bancaria';
    default:
      throw new Error(`Payment method ${paymentMethod} has no name implemented yet.`);
  }
}

function EcuadorianMethodName({ paymentMethod }: CountryPaymentMethodNameProps): Node {
  switch (paymentMethod) {
    case 'safetypay-cash':
      return 'Efectivo';
    case 'safetypay-online':
      return 'Transferencia';
    case 'creditcard':
      return 'Tarjeta de crédito';
    case 'debitcard':
      return 'Tarjeta de débito';
    default:
      throw new Error(`Payment method ${paymentMethod} has no name implemented yet.`);
  }
}

function UruguayanMethodName({ paymentMethod }: CountryPaymentMethodNameProps): Node {
  switch (paymentMethod) {
    case 'creditcard':
      return 'Tarjeta de Crédito';

    case 'debitcard':
      return 'Tarjeta de Débito';

    default:
      throw new Error(`Payment method ${paymentMethod} has no name implemented yet.`);
  }
}

function PanamanianMethodName({ paymentMethod }: CountryPaymentMethodNameProps): Node {
  switch (paymentMethod) {
    case 'creditcard':
      return 'Tarjeta de Crédito';

    case 'debitcard':
      return 'Tarjeta de Débito';

    case 'cash':
      return 'PuntoXpress';

    default:
      throw new Error(`Payment method ${paymentMethod} has no name implemented yet.`);
  }
}

function ElSalvadorMethodName({ paymentMethod }: CountryPaymentMethodNameProps): Node {
  switch (paymentMethod) {
    case 'creditcard':
      return 'Tarjeta de Crédito';

    case 'debitcard':
      return 'Tarjeta de Débito';

    case 'cash':
      return 'PuntoXpress';

    default:
      throw new Error(`Payment method ${paymentMethod} has no name implemented yet.`);
  }
}

function GuatemalanMethodName({ paymentMethod }: CountryPaymentMethodNameProps): Node {
  switch (paymentMethod) {
    case 'creditcard':
      return 'Tarjeta de Crédito';

    case 'debitcard':
      return 'Tarjeta de Débito';

    case 'cash':
      return 'PuntoXpress';

    default:
      throw new Error(`Payment method ${paymentMethod} has no name implemented yet.`);
  }
}

function DominicanMethodName({ paymentMethod }: CountryPaymentMethodNameProps): Node {
  switch (paymentMethod) {
    case 'creditcard':
      return 'Tarjeta de Crédito';

    case 'debitcard':
      return 'Tarjeta de Débito';

    default:
      throw new Error(`Payment method ${paymentMethod} has no name implemented yet.`);
  }
}

function CostaRicanMethodName({ paymentMethod }: CountryPaymentMethodNameProps): Node {
  switch (paymentMethod) {
    case 'creditcard':
      return 'Tarjeta de Crédito';

    case 'debitcard':
      return 'Tarjeta de Débito';

    case 'cash':
      return 'PuntoXpress';

    default:
      throw new Error(`Payment method ${paymentMethod} has no name implemented yet.`);
  }
}

function ParaguayanMethodName({ paymentMethod }: CountryPaymentMethodNameProps): Node {
  switch (paymentMethod) {
    case 'creditcard':
      return 'Tarjeta de Crédito';

    case 'debitcard':
      return 'Tarjeta de Débito';

    default:
      throw new Error(`Payment method ${paymentMethod} has no name implemented yet.`);
  }
}

function SouthAfricanMethodName({ paymentMethod }: CountryPaymentMethodNameProps): Node {
  switch (paymentMethod) {
    case 'creditcard':
      return 'Credit card';

    case 'debitcard':
      return 'Debit card';

    case 'externalpayprovider':
      return 'OZOW (EFT)';

    default:
      throw new Error(`Payment method ${paymentMethod} has no name implemented yet.`);
  }
}

function JamaicaMethodName({ paymentMethod }: CountryPaymentMethodNameProps): Node {
  switch (paymentMethod) {
    case 'creditcard':
      return 'Credit card';

    case 'debitcard':
      return 'Debit card';

    default:
      throw new Error(`Payment method ${paymentMethod} has no name implemented yet.`);
  }
}

function NigerianMethodName({ paymentMethod }: CountryPaymentMethodNameProps): Node {
  switch (paymentMethod) {
    case 'banktransfer':
    case 'nibbs':
      return 'Bank Transfer';

    case 'creditcard':
      return 'Credit card';

    case 'debitcard':
      return 'Debit card';

    case 'ewallet':
      return 'USSD';

    case 'externalpayprovider':
      return 'OPay';

    default:
      throw new Error(`Payment method ${paymentMethod} has no name implemented yet.`);
  }
}

function KenyanMethodName({ paymentMethod }: CountryPaymentMethodNameProps): Node {
  switch (paymentMethod) {
    case 'ewallet':
      return 'Mobile Money';

    case 'creditcard':
      return 'Credit card';

    case 'debitcard':
      return 'Debit card';

    default:
      throw new Error(`Payment method ${paymentMethod} has no name implemented yet.`);
  }
}

function EgyptMethodName({ paymentMethod }: CountryPaymentMethodNameProps): Node {
  switch (paymentMethod) {
    case 'creditcard':
      return translate('volcanes.payment-methods.card.methodName.creditcard');

    case 'debitcard':
      return translate('volcanes.payment-methods.card.methodName.debitcard');

    case 'externalpayprovider':
      return translate('volcanes.payment-methods.card.methodName.externalpayprovider');

    default:
      throw new Error(`Payment method ${paymentMethod} has no name implemented yet.`);
  }
}

function IndiaMethodName({ paymentMethod }: CountryPaymentMethodNameProps): Node {
  switch (paymentMethod) {
    case 'ewallet':
      return 'UPI';

    case 'creditcard':
      return 'Credit card';

    case 'debitcard':
      return 'Debit card';

    default:
      throw new Error(`Payment method ${paymentMethod} has no name implemented yet.`);
  }
}

function MobileMoneyMethodName({ paymentMethod }: CountryPaymentMethodNameProps): Node {
  switch (paymentMethod) {
    case 'ewallet':
      return 'Mobile Money';

    default:
      throw new Error(`Payment method ${paymentMethod} has no name implemented yet.`);
  }
}
